import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { logout } from 'service/cognitoService';
import { getAuthCookie } from 'utils/auth';

/**
 * Decisions
 * - Managing filtering client side
 *   - Simplifies mutations
 *   - Simplifies reconciling notifications when SSE lands
 * - Capping paging
 *   - Simplifies mutations  
 *   - Simplifies reconciling notifications when SSE lands
 *   - If we decide that we need to handle > 100 notifications.
 *      - Split to using server side paging.
 *      - Complex mutation reconciliation
 *      - Complex SSE reconciliation
 * Alternatives
 * - Invalidate queries on mutations
 * - Invalidate queries when SSE comes comes through
 * 
 * To do:
 * - Add SSE handling
 *   - Update the queryData cache when an event comes in
 *   - Remove refetch_interval
 */
const BASE_URL = process.env.NEXT_PUBLIC_NOTIFICATIONS_API_ENDPOINT || '';

export type NotificationOut = {
  id: string;
  type: string;
  workspace_id: string;
  payload: Record<string, unknown>;
  created_at: string;
  expires_at: string | null;
  resolved: boolean;
  read_at: string | null;
};

type NotificationListOut = {
  data: NotificationOut[];
  metadata: {
    offset: number;
    limit: number;
  };
};

function getAuthHeader(): Record<string, string> {
  const cookie = getAuthCookie();
  if (cookie?.id_token) {
    return { Authorization: `Bearer ${cookie.id_token}` };
  }
  return {};
}

function handleResponseError(response: Response, message: string): never {
  if (response.status === 401) {
    logout();
  }
  throw new Error(message);
}

async function fetchNotifications(): Promise<NotificationOut[]> {
  const params = new URLSearchParams({ limit: '100' });
  const response = await fetch(`${BASE_URL}/api/notifications?${params}`, {
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
  });
  if (!response.ok) {
    handleResponseError(response, 'Failed to fetch notifications');
  }
  const result: NotificationListOut = await response.json();
  return result.data;
}

async function patchNotification(id: string, read: boolean): Promise<void> {
  const response = await fetch(`${BASE_URL}/api/notifications/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ read }),
  });
  if (!response.ok) {
    handleResponseError(response, 'Failed to update notification');
  }
}

async function patchAllNotifications(): Promise<void> {
  const response = await fetch(`${BASE_URL}/api/notifications`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ read: true }),
  });
  if (!response.ok) {
    handleResponseError(response, 'Failed to mark all notifications as read');
  }
}

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

const selectUnread = (data: NotificationOut[]) => data.filter((n) => n.read_at === null);

export function useNotifications(unreadOnly = false) {
  return useQuery<NotificationOut[]>({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: fetchNotifications,
    select: unreadOnly ? selectUnread : undefined,
    refetchInterval: 20_000, // Remove when SSE lands
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patchNotification(id, true),
    onSuccess: (_, id) => {
      const readAt = new Date().toISOString();
      queryClient.setQueryData<NotificationOut[]>(
        NOTIFICATIONS_QUERY_KEY,
        (old = []) => old.map((n) => (n.id === id ? { ...n, read_at: readAt } : n))
      );
    },
  });
}

export function useMarkNotificationUnread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patchNotification(id, false),
    onSuccess: (_, id) => {
      queryClient.setQueryData<NotificationOut[]>(
        NOTIFICATIONS_QUERY_KEY,
        (old = []) => old.map((n) => (n.id === id ? { ...n, read_at: null } : n))
      );
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchAllNotifications,
    onSuccess: () => {
      const readAt = new Date().toISOString();
      queryClient.setQueryData<NotificationOut[]>(
        NOTIFICATIONS_QUERY_KEY,
        (old = []) => old.map((n) => ({ ...n, read_at: n.read_at ?? readAt }))
      );
    },
  });
}
